import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { apiGetInvite, formatApiErrorDetail } from "@/lib/api";
import { Check } from "lucide-react";

export default function Welcome() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { acceptInvite } = useAuth();

  const [phase, setPhase] = useState("checking"); // checking | ready | invalid
  const [info, setInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setPhase("invalid"); setError("This invitation link is missing its token."); return; }
    apiGetInvite(token)
      .then((d) => { setInfo(d); setPhase("ready"); })
      .catch((e) => { setPhase("invalid"); setError(formatApiErrorDetail(e?.response?.data?.detail) || "This link is no longer valid."); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    setBusy(true);
    try {
      await acceptInvite(token, password);
      navigate("/hub");
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="exec-scope min-h-screen" data-testid="welcome-page">
      <Seo title="Welcome — Copper & Vine" description="Activate your Copper & Vine account." noindex />
      <section className="exec-container pt-24 md:pt-28 pb-24">
        <div className="max-w-xl">
          <p className="exec-eyebrow">The Hub</p>

          {phase === "checking" && (
            <p className="exec-serif text-[17px] text-[color:var(--grey)] mt-6" data-testid="welcome-checking">Checking your invitation…</p>
          )}

          {phase === "invalid" && (
            <div data-testid="welcome-invalid">
              <h1 className="font-light text-[clamp(26px,3.2vw,38px)] leading-[1.14] mt-4">This invitation isn’t active.</h1>
              <p className="exec-serif text-[17px] text-[color:var(--lead-ink)] mt-4 border-l-2 border-[color:var(--copper)] pl-4">{error}</p>
              <Link to="/login" className="exec-btn exec-btn-secondary mt-7">Go to sign in</Link>
            </div>
          )}

          {phase === "ready" && (
            <div data-testid="welcome-ready">
              <h1 className="font-light text-[clamp(28px,3.6vw,42px)] leading-[1.14] mt-4">Welcome to Copper <span className="exec-amp">&amp;</span> Vine.</h1>
              <p className="exec-serif text-[17px] leading-[1.55] text-[color:var(--lead-ink)] mt-5">
                {info?.name ? `${info.name.split(" ")[0]}, set` : "Set"} a password for{" "}
                <span className="text-[color:var(--ink)]">{info?.email}</span> to open your Hub
                {info?.company ? ` for ${info.company}` : ""}.
              </p>

              <form onSubmit={submit} className="mt-8 max-w-md">
                <label className="block">
                  <span className="block text-[11px] uppercase tracking-[0.16em] font-normal text-[color:var(--grey)]">Create a password</span>
                  <input
                    data-testid="welcome-password"
                    type="password"
                    className="exec-underline mt-2"
                    placeholder="At least 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                </label>
                {error && (
                  <p data-testid="welcome-error" className="exec-serif text-[16px] text-[color:var(--ink)] border-l-2 border-[color:var(--copper)] pl-3 mt-3">
                    {error}
                  </p>
                )}
                <button data-testid="welcome-submit" type="submit" disabled={busy}
                  className="exec-btn exec-btn-primary mt-6 disabled:opacity-60">
                  {busy ? "Setting up…" : <>Open my Hub <Check size={15} /></>}
                </button>
              </form>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
